import { AxiosError } from 'axios';
import { inject, injectable, interfaces } from 'inversify';
import { ICurrentWorkspaceStore } from '../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { browserHistory } from '../../usehistory';
import { clearPageCache } from '../helpers/cache.helpers';
import { GLOBAL_ERROR_CODES } from '../helpers/global.constants';
import { ACDLError, ApiErrorResponse, ForbiddenError, LockedUserError, UnauthorizedError, VersionMissmatchError } from '../models/error.model';
import { IAuthStore } from '../modules/auth/auth.store';
import { IErrorsService } from '../modules/errors/errors.service';
import { PolicyLimitExceededNotification, ToManyRequestsNotification } from '../modules/notifications/models/notification.model';
import { INotificationsStore } from '../modules/notifications/stores/notifications.store';
import { Home } from '../routes/config/Home';

export type IPolicy = Record<number, (error: AxiosError<ApiErrorResponse>) => Promise<ACDLError>>;

export interface IApiPolicyProvider {
  getGlobal(): IPolicy;
}

export namespace IApiPolicyProvider {
  export const $: interfaces.ServiceIdentifier<IApiPolicyProvider> = Symbol('API_POLICY_PROVIDER');
}

@injectable()
export class ApiPolicyProvider implements IApiPolicyProvider {
  constructor(
    @inject(IAuthStore.$) public readonly authStore: IAuthStore,
    @inject(INotificationsStore.$) private readonly notificationStore: INotificationsStore,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IErrorsService.$) private readonly errorsService: IErrorsService
  ) {}

  getGlobal(): IPolicy {
    const policy: IPolicy = {};

    policy[400] = async (error: AxiosError<ApiErrorResponse>): Promise<ACDLError> => {
      if (error.response?.data) {
        const errorCodes = error.response.data.errorCodes;

        if (errorCodes && errorCodes instanceof Array && errorCodes.length > 0) {
          if (errorCodes.includes(GLOBAL_ERROR_CODES.POLICY_LIMITS_EXCEEDED)) {
            this.notificationStore.notifications.push(new PolicyLimitExceededNotification());
            browserHistory.push(Home.Projects.List.All.withParams({ workspaceId: this.currentWorkspaceStore.currentWorkspace.id }));
          }
        }
      }

      return Promise.resolve(new ACDLError('Bad Request', error.response?.data));
    };

    policy[401] = async (): Promise<ACDLError> => {
      this.authStore.clearToken();
      this.authStore.setRouteReferrer(browserHistory.location.state as string);
      return Promise.resolve(new UnauthorizedError());
    };

    policy[402] = async (): Promise<ACDLError> => {
      return Promise.resolve(new ForbiddenError());
    };

    policy[403] = async (): Promise<ACDLError> => {
      this.errorsService.setUnauthorized();
      return Promise.resolve(new ForbiddenError());
    };

    policy[417] = async (): Promise<ACDLError> => {
      this.authStore.setVersionChanged();
      this.authStore.clearToken();
      await clearPageCache(true);
      return new VersionMissmatchError();
    };

    policy[429] = async (error: AxiosError<ApiErrorResponse>): Promise<ACDLError> => {
      this.notificationStore.notifications.push(new ToManyRequestsNotification());
      return Promise.resolve(new ACDLError('Too Many Requests', error.response?.data));
    };

    policy[433] = async (): Promise<ACDLError> => {
      this.errorsService.setUnauthorized();
      return Promise.resolve(new ForbiddenError());
    };

    policy[460] = async (): Promise<ACDLError> => {
      browserHistory.push(Home.Locked.Path);
      return Promise.resolve(new LockedUserError());
    };

    return policy;
  }
}
